<template>
  <div id="app">
    <AnnouncementBanners />
    <AppHeader :style="{ top: bannerHeight ? `${bannerHeight}px` : undefined }" />
    <FlashMessage />
    <Toast />
    <router-view name="sidebar"></router-view>
    <main id="main" class="h-screen" :style="{ paddingTop: totalOffset ? `${totalOffset}px` : undefined }">
      <ErrorBoundary class="h-full">
        <VersionNotifier />
        <VersionDebugPanel />
        <router-view />
      </ErrorBoundary>
      <DisclaimerModal disclaimer-type="LEGAL" v-if="showLegalDisclaimer()" />
      <ClientViewsModal v-if="showClientViewsModal" :show="showClientViewsModal" @enable-client-view="$router.push('/home')" />
    </main>
    <ClientViewOverlay v-if="isClientViewEnabled" />
    <SessionExpiredModal />
  </div>
</template>

<script lang="ts">
import { provide } from 'vue';
import AppHeader from '@/components/Header.vue';
import AnnouncementBanners from '@/components/AnnouncementBanners.vue';
import FlashMessage from '@/components/FlashMessage.vue';
import Toast from '@/components/v2/Toast.vue';
import VersionNotifier from '@/components/VersionNotifier.vue';
import VersionDebugPanel from '@/components/VersionDebugPanel.vue';
import ErrorBoundary from '@/components/ErrorBoundary.vue';
import AppStore, { AppState, ResizerRegistryItem } from '@/store';
import { bootstrap, config } from 'vue-gtag';
import { Store, useStore } from 'vuex';
import DisclaimerModal from './components/DisclaimerModal.vue';
import { ActivityMixin } from '@/utilities/Activity';
import ClientViewOverlay from './components/ClientViewOverlay.vue';
import { computed } from 'vue';
import ClientViewsModal from './components/ClientViewsModal.vue';
import SessionExpiredModal from './components/SessionExpiredModal.vue';
import { registerResizeItem } from './utilities/resize';
import { IsUserAClient } from './utilities/AuthUtils';
import { useHeaderOffset } from '@/composables/useHeaderOffset';
import { useSessionExpiry } from '@/composables/useSessionExpiry';

export default {
  components: {
    ErrorBoundary,
    VersionNotifier,
    VersionDebugPanel,
    AppHeader,
    AnnouncementBanners,
    FlashMessage,
    Toast,
    DisclaimerModal,
    ClientViewOverlay,
    ClientViewsModal,
    SessionExpiredModal,
  },
  mixins: [ActivityMixin],
  methods: {
    handleResize() {
      for (const item of Object.values(this.store.state.resizerRegistry) as ResizerRegistryItem[]) {
        item.resizeFunc?.call(self, item.name, item.selector);
      }
    },
    showLegalDisclaimer(): boolean {
      const user = AppStore.state.user;
      if (user === undefined) {
        return false;
      }

      return IsUserAClient(user) && !user.legalDisclaimerAccepted;
    },
  },
  async created() {
    provide<Store<AppState>>('store', AppStore);
    const user = AppStore.state.user;
    const enableGtag = (): void => {
      bootstrap().then(() => {
        config({ user_properties: { platform_id: user?.user.id } });
      });
    };
    if (user) {
      enableGtag();
    }

    addEventListener('resize', this.handleResize);
  },
  mounted() {
    registerResizeItem({ name: 'app', selector: 'app' });
    this.handleResize();
  },
  setup() {
    const store = useStore<AppState>();
    const isClientViewEnabled = computed<boolean>(() => AppStore.state.clientViewState.isClientViewEnabled);
    const showClientViewsModal = computed<boolean>(() => AppStore.state.clientViewState.showClientViewsModal);
    
    const { bannerHeight, totalOffset } = useHeaderOffset();
    useSessionExpiry().watchForExpiredSession();

    return { store, isClientViewEnabled, showClientViewsModal, bannerHeight, totalOffset };
  },
};
</script>

<style lang="scss" scoped>
@import 'assets/styles/global.css';

#app {
  font-size: 14px;
  color: inherit;
}
</style>
